import React, { useEffect, useState } from "react"
import { Container, Row, Col, Button, Card, Form } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import { getSinesters } from "../actions/other/sinestersActions"
import { RootState, AppDispatch } from "../store"

import { CustomDataTable } from "../components/design/Datatables/CustomDataTable"
import Loader from "../components/loaders/Loader"

export interface Sinester {
	_id?: string
	id?: string
	refCia?: string
	abrevCia?: string
	tomador?: string
	compania?: string
	fecha?: string
	fechaApertura?: string
	fechaCierre?: string
	fechaEfecto?: string
	estado?: string
	descripcion?: string
	descripcionProducto?: string
	arrayNotes?: any[]
}

export default function Sinesters() {
	const { t } = useTranslation()
	const dispatch: AppDispatch = useDispatch()
	const navigate = useNavigate()

	const { sinesters, loading, total } = useSelector((state: RootState) => state.sinesters)

	const [page, setPage] = useState(1)
	const [limit, setLimit] = useState(25)
	const [search, setSearch] = useState<any>({})
	const [sort, setSort] = useState<any>(null)

	const [numero, setNumero] = useState("")
	const [nifCliente, setNifCliente] = useState('')
	const [nombreCliente, setNombreCliente] = useState("")
	const [matriculaAuto, setMatriculaAuto] = useState("")
	const [fecha, setFecha] = useState("")

	const [totalRows, setTotalRows] = useState(0)

	useEffect(() => {
		setTotalRows(total || 0)
	}, [total])

	const selectSinester = (sinester: Sinester) => {
		navigate(`${sinester.id}`)
	}

	const handleSearch = () => {
		dispatch(getSinesters(1, 999999, search) as any)
	}

	const columnsSinester = [
		{
			name: t("sinesterTranslate.tomador"),
			id: "tomador",
			cell: (sinester: Sinester) => (
				<div className="cursor text-start" onClick={() => selectSinester(sinester)}>
					{sinester?.tomador || "—"}
				</div>
			),
			selector: (sinester: Sinester) => sinester?.tomador || "—",
			sortable: true,
		}, {
			name: t("sinesterTranslate.compania"),
			id: "abrevCia",
			cell: (sinester: Sinester) => (
				<div className="cursor text-start" onClick={() => selectSinester(sinester)}>
					{sinester?.abrevCia || "—"}
				</div>
			),
			selector: (sinester: Sinester) => sinester?.abrevCia || "—",
			sortable: true,
		}, {
			name: t("sinesterTranslate.refCia"),
			id: "refCia",
			cell: (sinester: Sinester) => (
				<div className="cursor text-start" onClick={() => selectSinester(sinester)}>
					{sinester?.refCia || "—"}
				</div>
			),
			selector: (sinester: Sinester) => sinester?.refCia || "—",
			sortable: true,
		}, {
			name: t("sinesterTranslate.descripcionProducto"),
			id: "descripcionProducto",
			cell: (sinester: Sinester) => (
				<div className="cursor text-start" onClick={() => selectSinester(sinester)}>
					{sinester?.descripcionProducto || "—"}
				</div>
			),
			selector: (sinester: Sinester) => sinester?.descripcionProducto || "—",
			sortable: true,
		}, {
			name: t("sinesterTranslate.descripcion"),
			id: "descripcion",
			cell: (sinester: Sinester) => (
				<div className="cursor text-start" onClick={() => selectSinester(sinester)}>
					{sinester?.descripcion || "—"}
				</div>
			),
			selector: (sinester: Sinester) => sinester?.descripcion || "—",
			sortable: true,
		}, {
			name: t("sinesterTranslate.estado"),
			cell: (sinester: Sinester) => (
				<div className="cursor text-start" onClick={() => selectSinester(sinester)}>
					{sinester?.estado || "—"}
				</div>
			)
		}, {
			name: t("sinesterTranslate.fecha"),
			cell: (sinester: Sinester) => (
				<div className="cursor text-start" onClick={() => selectSinester(sinester)}>
					{sinester?.fecha ?
						new Intl.DateTimeFormat("es-ES", { year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(sinester.fecha))
						:
						"—"
					}
				</div>
			)
		}, {
			name: t("sinesterTranslate.fechaApertura"),
			cell: (sinester: Sinester) => (
				<div className="cursor text-start" onClick={() => selectSinester(sinester)}>
					{sinester?.fechaApertura ?
						new Intl.DateTimeFormat("es-ES", { year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(sinester.fechaApertura))
						:
						"—"
					}
				</div>
			)
		}
	]

	return (
		<Container fluid className="p-1 py-5 p-md-5">
			<Row>
				<Col xs={12}>
					<h1>{t("menus.sinesters")}</h1>
				</Col>
			</Row>

			<Row>
				<Col xs={12} className="my-3">
					<Card>
						<Card.Body>
							<Row className="justify-content-between">
								<Col xs={10}>
									<Row>
										<Col xs={6} lg={3} className="mb-3">
											<Form.Control
												placeholder={t("sinesterTranslate.numPoliza")}
												value={numero}
												onChange={e => {
													setNumero(e.target.value)
													setSearch({ ...search, numero: e.target.value })
												}}
											/>
										</Col>
										<Col xs={6} lg={3} className="mb-3">
											<Form.Control
												className="text-uppercase"
												type="text"
												placeholder={t("sinesterTranslate.nifCliente")}
												value={nifCliente || ""}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													const val = e.target.value.toUpperCase();
													setNifCliente(val);
													setSearch({ ...search, nifCliente: val });
													setPage(1);
												}}
												onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
													if (e.key === "Enter") {
														setSearch({ ...search, nifCliente: nifCliente });
														setPage(1);
														handleSearch();
													}
												}}
											/>
										</Col>
										<Col xs={6} lg={6} className="mb-3">
											<Form.Control
												className="text-uppercase"
												type="text"
												placeholder={t("sinesterTranslate.nombreCliente")}
												value={nombreCliente || ""}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													const val = e.target.value.toUpperCase();
													setNombreCliente(val);
													setSearch({ ...search, nombreCliente: val });
													setPage(1);
												}}
												onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
													if (e.key === "Enter") {
														setSearch({ ...search, nombreCliente: nombreCliente });
														setPage(1);
														handleSearch();
													}
												}}
											/>
										</Col>
										<Col xs={6} lg={3}>
											<Form.Control
												className="text-uppercase"
												type="text"
												placeholder={t("sinesterTranslate.matriculaAuto")}
												value={matriculaAuto || ""}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													const val = e.target.value.toUpperCase();
													setMatriculaAuto(val);
													setSearch({ ...search, matriculaAuto: val });
													setPage(1);
												}}
												onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
													if (e.key === "Enter") {
														setSearch({ ...search, matriculaAuto: matriculaAuto });
														setPage(1);
														handleSearch();
													}
												}}
											/>
										</Col>
										<Col xs={6} lg={3}>
											<Form.Control
												type="text"
												placeholder={t("sinesterTranslate.fecha")}
												value={fecha || ""}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													setFecha(e.target.value);
													setSearch({ ...search, fecha: e.target.value });
													setPage(1);
												}}
												onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
													if (e.key === "Enter") {
														setSearch({ ...search, fecha: fecha });
														setPage(1);
														handleSearch();
													}
												}}
											/>
										</Col>
									</Row>
								</Col>
								<Col xs={2} className="text-end">
									<Button onClick={handleSearch} className="button-search">
										<i className="fas fa-search" />
									</Button>
								</Col>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>

			<Row>
				<Col>
					<Card>
						<Card.Body>
							{loading ? (
								<Loader />
							) : sinesters?.length ? (
								<CustomDataTable
									columns={columnsSinester}
									data={sinesters}
									pagination
								/>
							) : (
								<h4>{t("sinesterTranslate.thereisnotsinester")}</h4>
							)}
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</Container>
	)
}
